import React from "react";
import { Money } from "@/helpers/Money";
import { growthBook } from "@/helpers/growthbook";
import { MarketingIcon } from "@/components/icons";
import { productSelect, variantSelect } from "@/store";
import { getEffectiveSubscriptionDiscountPercent } from "@/helpers/constants";

// Types for clarity
export type PromoEntry = {
  collection: string;
  discountType: string;
  discountValue: number;
  discountCode: string;
};

export type Collection = { id: string; handle: string; title: string };

export function getFinalAtcCtaPrice({
  productCollections,
  basePriceCents,
  sellingPlan,
  subscription,
  variant,
  product,
  currency = "$",
  strikeThroughSize,
  priceSize,
  strikeThroughColor,
  priceColor,
  bannerPadding,
}: {
  productCollections: Collection[] | undefined;
  basePriceCents: number | undefined;
  sellingPlan: any;
  subscription: any;
  variant: any;
  product?: any;
  currency?: "$" | "¢";
  strikeThroughSize?: string;
  priceSize?: string;
  strikeThroughColor?: string;
  priceColor?: string;
  bannerPadding?: string;
}): {
  display: React.ReactNode;
  discounted: string | null;
  banner: React.ReactNode | null;
  currentPrice: React.ReactNode;
} {
  const promoEntries: PromoEntry[] = (window as any).promoDiscountEntries || [];

  // Determine flags from product context
  const isBundle =
    !!product &&
    (product.type === "Bundle" ||
      !!product.bundledItems?.length ||
      product.tags?.includes("bundle"));
  const isBulk = !!product && productSelect.isBulk(product);

  // Find the best matching promo
  const matchingPromo = promoEntries
    .filter((entry) =>
      productCollections?.some(
        (col) => col.handle === entry.collection || col.id === entry.collection
      )
    )
    .sort((a, b) => b.discountValue - a.discountValue)[0];

  const isPromoDiscountEligible = matchingPromo || false;

  // If test is off or no promo, return the normal price
  if (!isPromoDiscountEligible) {
    // Use centralized logic for subscription price
    const subscriptionPrice = variantSelect.lowestPrice(variant, product);
    return {
      display: sellingPlan
        ? subscriptionPrice.format()
        : variant.price.format(),
      discounted: null,
      banner: null,
      currentPrice: sellingPlan
        ? (() => {
            // Apply global discount utility for subscription price
            if (subscription?.discount.kind === "percentage") {
              const percent = getEffectiveSubscriptionDiscountPercent(
                subscription.discount.percentage,
                { isBulk, isBundle }
              );
              return variant.price.mul(1 - percent / 100).format();
            }
            return subscription?.discount.price.format();
          })()
        : variant.price.format(),
    };
  }

  // Calculate discounted price
  const {
    discountType,
    discountValue,
    discountCode = "",
  } = isPromoDiscountEligible;
  const type = discountType?.toLowerCase();

  let finalPrice = basePriceCents ?? 0;

  if (type === "percentage") {
    finalPrice = Math.ceil(finalPrice * (1 - (discountValue ?? 0) / 100));
  } else if (type === "fixed") {
    finalPrice = Math.max(
      0,
      finalPrice - Math.ceil((discountValue ?? 0) * 100)
    );
  }

  const formattedDiscounted = Money.from(finalPrice / 100, currency).format();
  // Use centralized logic for subscription price
  const subscriptionPrice = variantSelect.lowestPrice(variant, product);
  const originalPrice = sellingPlan
    ? (() => {
        // Apply global discount utility for subscription price
        if (subscription?.discount.kind === "percentage") {
          const percent = getEffectiveSubscriptionDiscountPercent(
            subscription.discount.percentage,
            { isBulk, isBundle }
          );
          return variant.price.mul(1 - percent / 100).format();
        }
        return subscriptionPrice.format();
      })()
    : variant.price.format();

  // Banner JSX if eligible
  let banner: React.ReactNode = null;
  if (discountValue && discountCode) {
    const discountText =
      type === "fixed" ? `$${discountValue} OFF` : `${discountValue}% OFF`;

    banner = (
      <div
        className={`mt-4 flex items-center justify-center rounded-[3px] border border-solid border-secondary-500 bg-secondary-100 ${
          bannerPadding || "px-4 py-[10px]"
        }`}
      >
        <MarketingIcon />
        <p className="ml-[10px] text-left text-xs">
          You`re getting <strong>{discountText}</strong> with code{" "}
          <strong>{discountCode}</strong> auto-applied
        </p>
      </div>
    );
  }

  // Return display as React.createElement (for .ts file compatibility)
  return {
    display: (
      <>
        <span
          className={`mr-2 font-normal line-through ${strikeThroughSize} ${strikeThroughColor} group-hover:text-white`}
        >
          {originalPrice}
        </span>
        <span className={`${priceSize} ${priceColor} group-hover:text-white`}>
          {formattedDiscounted}
        </span>
      </>
    ),
    discounted: formattedDiscounted,
    banner,
    currentPrice: originalPrice,
  };
}
